/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import {
  useContext,
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useNodesState, useEdgesState } from "reactflow";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import useGetMetadata from "@/hooks/useGetMetaData";
import { useTheme } from "@mui/material/styles";
import { Stack, Chip, Avatar, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import RootIcon from "@mui/icons-material/Home";
import StepIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

const PtwContext = createContext();
export const PtwSettingsData = () => useContext(PtwContext);

const StyledNode = styled("div")(({ theme, isRoot, isDarkMode }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: isRoot
    ? theme.palette.primary.main
    : isDarkMode
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  color: theme.palette.getContrastText(
    isRoot
      ? theme.palette.primary.main
      : isDarkMode
        ? theme.palette.grey[900]
        : theme.palette.grey[100]
  ),
  borderRadius: "16px",
  border: `2px solid ${
    isRoot
      ? theme.palette.primary.dark
      : isDarkMode
        ? theme.palette.grey[700]
        : theme.palette.grey[300]
  }`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: theme.shadows[4],
  position: "relative",
  padding: theme.spacing(2),
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
  },
}));

export const PtwSettingsWrapper = ({ children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    WorkFlowList,
    WorkFlows: initialWorkflows,
    userGroupsList,
    inputTypes,
    refetchWorkflows: fetchWorkflowsFromServer,
    isLoading,
  } = useGetMetadata() || {};

  const [WorkFlows, setWorkFlows] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  // eslint-disable-next-line no-unused-vars
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { setItem } = useLocalStorage("workflowdetails");
  const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === "dark");
  const [workflowAction, setWorkflowAction] = useState("list");

  const defaultEdgeOptions = useMemo(
    () => ({
      animated: true,
      type: "smoothstep",
      style: {
        stroke: theme.palette.primary.dark,
        strokeWidth: 2,
      },
    }),
    [theme.palette.primary.dark]
  );

  // Sync WorkFlows with initialWorkflows
  useEffect(() => {
    if (initialWorkflows) {
      setWorkFlows(initialWorkflows);
    }
  }, [initialWorkflows]);

  // Refresh workflows from server
  const refreshWorkflows = useCallback(async () => {
    await fetchWorkflowsFromServer();
  }, [fetchWorkflowsFromServer]);

  // Memoize inputTypeList to prevent unnecessary updates
  const inputTypeList = useMemo(
    () =>
      inputTypes?.map(({ id, display_name, input_key }) => ({
        value: id,
        label: display_name,
        input_key,
      })) || [],
    [inputTypes]
  );

  // Update dark mode based on theme
  useEffect(() => {
    setIsDarkMode(theme.palette.mode === "dark");
  }, [theme.palette.mode]);

  // Reset state when returning to list view
  const goBackToList = useCallback(() => {
    setWorkflowAction("list");
    setSelectedWorkflow(null);
    setItem(null);
    // Explicitly remove from localStorage to ensure it's cleared
    localStorage.removeItem("workflowdetails");
    setNodes([]);
    setEdges([]);
  }, [setItem, setNodes, setEdges]);

  // Handle workflow selection
  const handleSelectChange = useCallback(
    (selectedOption) => {
      setSelectedOption(selectedOption);
      const selectedObject = WorkFlows.find(
        (obj) => obj.id === selectedOption?.value
      );

      setSelectedWorkflow(selectedObject);
      setItem(selectedObject);
    },
    [WorkFlows, setItem]
  );

  // Create nodes and edges for preview/edit modes
  const createElements = useCallback(() => {
    // Skip rendering for duplicate action to avoid conflicts
    if (workflowAction === "duplicate") {
      setNodes([]);
      setEdges([]);
      return;
    }

    if (!selectedWorkflow) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Validate that steps array exists and is an array
    if (!selectedWorkflow.steps || !Array.isArray(selectedWorkflow.steps)) {
      // Create only the root node if no steps exist
      setNodes([
        {
          id: selectedWorkflow.id,
          style: {
            background: "transparent",
            border: "none",
            width: 240,
            height: 120,
            zIndex: 1,
          },
          data: {
            label: (
              <StyledNode isRoot isDarkMode={isDarkMode}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.dark,
                      width: 28,
                      height: 28,
                    }}
                  >
                    <RootIcon fontSize="small" />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {selectedWorkflow.name || t("ptwSettings.rootNodeLabel")}
                  </Typography>
                </Stack>
              </StyledNode>
            ),
          },
          position: { x: 0, y: -140 },
        },
      ]);
      setEdges([]);
      return;
    }

    const nodes = [];
    const edges = [];
    const parentChildMap = new Map();

    // Root node
    nodes.push({
      id: selectedWorkflow.id,
      type: "default",
      sourcePosition: "bottom",
      targetPosition: "top",
      style: {
        background: "transparent",
        border: "none",
        width: 240,
        height: 120,
        zIndex: 1,
      },
      data: {
        label: (
          <StyledNode isRoot isDarkMode={isDarkMode}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.dark,
                  width: 28,
                  height: 28,
                }}
              >
                <RootIcon fontSize="small" />
              </Avatar>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                textAlign="center"
              >
                {selectedWorkflow.name || t("ptwSettings.rootNodeLabel")}
              </Typography>
            </Stack>
          </StyledNode>
        ),
      },
      position: { x: 0, y: -140 },
    });

    // Build parent-child relationships
    selectedWorkflow.steps.forEach((step) => {
      const parentId = step.parent_id || selectedWorkflow.id;
      if (!parentChildMap.has(parentId)) {
        parentChildMap.set(parentId, []);
      }
      parentChildMap.get(parentId).push(step);
    });

    const levelPositions = {};

    // Process nodes by level
    const processLevel = (parentId, level = 0) => {
      if (!parentChildMap.has(parentId)) return;

      if (!levelPositions[level]) {
        levelPositions[level] = 0;
      }

      parentChildMap.get(parentId).forEach((step) => {
        const nodeId = step.id;

        const x = levelPositions[level];
        const y = level * 220;

        nodes.push({
          id: nodeId,
          type: "default",
          sourcePosition: "bottom",
          targetPosition: "top",
          style: {
            background: "transparent",
            border: "none",
            width: 240,
            height: 120,
            zIndex: 1,
          },
          data: {
            label: (
              <StyledNode isDarkMode={isDarkMode}>
                <Stack
                  direction="column"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ position: "relative", width: "100%", height: "100%" }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.secondary.dark,
                        width: 24,
                        height: 24,
                      }}
                    >
                      <StepIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="body2">
                      {t("ptwSettings.actionLabel", {
                        actionName:
                          step.action_name || t("ptwSettings.unknownLabel"),
                      })}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      px: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? theme.palette.grey[900]
                          : theme.palette.grey[100],
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      align="center"
                    >
                      {t("ptwSettings.statusLabel", {
                        status:
                          step.status || t("ptwSettings.unknownStatusLabel"),
                      })}
                    </Typography>
                    {step.self_actionable && (
                      <Chip
                        label={t("ptwSettings.selfActionableLabel")}
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Stack>
              </StyledNode>
            ),
          },
          position: { x, y },
        });

        levelPositions[level] += 280;
        processLevel(nodeId, level + 1);
      });
    };

    processLevel(selectedWorkflow.id);

    // Create edges
    parentChildMap.forEach((childSteps, parentId) => {
      childSteps.forEach((step) => {
        const source = parentId;
        const target = step.id;
        edges.push({
          id: `edge-${source}-${target}`,
          source,
          target,
          type: "smoothstep",
          animated: true,
          style: {
            stroke: theme.palette.primary.dark,
            strokeWidth: 2,
          },
        });
      });
    });

    setNodes(nodes);
    setEdges(edges);
  }, [selectedWorkflow, isDarkMode, workflowAction, theme, t]);

  // Trigger node/edge creation when dependencies change
  useEffect(() => {
    createElements();
  }, [createElements]);

  // Memoize context value to prevent unnecessary updates
  const contextValue = useMemo(
    () => ({
      nodes,
      edges,
      WorkFlowList,
      selectedOption,
      onNodesChange,
      defaultEdgeOptions,
      handleSelectChange,
      setSelectedWorkflow,
      userGroupsList,
      WorkFlows,
      selectedWorkflow,
      inputTypeList,
      refetchWorkflows: refreshWorkflows,
      isLoading,
      workflowAction,
      setWorkflowAction,
      goBackToList,
    }),
    [
      nodes,
      edges,
      WorkFlowList,
      selectedOption,
      onNodesChange,
      defaultEdgeOptions,
      handleSelectChange,
      userGroupsList,
      WorkFlows,
      selectedWorkflow,
      inputTypeList,
      isLoading,
      workflowAction,
      refreshWorkflows,
      goBackToList,
    ]
  );

  return (
    <PtwContext.Provider value={contextValue}>
      <div
        style={{
          backgroundImage: isDarkMode
            ? `linear-gradient(145deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[900]} 100%)`
            : `linear-gradient(145deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[200]} 100%)`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </div>
    </PtwContext.Provider>
  );
};
